import { Component, h, Prop, Host, State, Listen, Method, Element, Event, EventEmitter } from '@stencil/core';

@Component({
    tag: 'sew-ws-flyout',
    styleUrl: 'sew-ws-flyout.scss',
    shadow: true,
})
export class SewWsFlyout {
    @Event() flyoutClosed: EventEmitter<void>;

    @Element() el: HTMLSewWsFlyoutElement;
    @Prop() icon: string;
    @Prop() iconLabel: string;
    @Prop({ reflect: true }) fullscreen = false;
    @Prop() counter: string;
    @Prop() username: string;
    @Prop() ignoreViewportWidth = false;
    @State() open: boolean;

    private slotElement: HTMLElement;

    @Listen('resize', { target: 'window' })
    onResize(): void {
        return;
    }

    @Listen('counter')
    onCounter(e: CustomEvent<string>): void {
        this.counter = e.detail === '0' ? null : e.detail;
    }

    /**
     * Tracks if any Element outside of the Component was clicked.
     * If this is the case, close the dropdown.
     * @param eventWindowClicked Event emitted on click on window
     */
    @Listen('mousedown', { target: 'window' })
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    checkForClickOutside(eventWindowClicked): void {
        const path =
            eventWindowClicked.path || (eventWindowClicked.composedPath && eventWindowClicked.composedPath());

        if (path.every(el => el !== this.el)) {
            this.closeFlyout();
        }
    }

    private toggleFlyout = () => {
        if (this.open) {
            this.closeFlyout();
        } else {
            this.openFlyout();
        }
    };

    private closeOtherFlyouts() {
        const header = document.querySelector('sew-ws-header');

        if (header) {
            const flyouts = header.shadowRoot.querySelectorAll('sew-ws-flyout');

            flyouts.forEach(flyout => {
                if (flyout !== this.el) {
                    flyout.closeFlyout();
                }
            });
        }
    }

    @Method()
    public async openFlyout(): Promise<void> {
        if (!this.open) {
            this.closeOtherFlyouts();
            this.flyoutElement.style.setProperty('--flyout-height', this.flyoutElement.scrollHeight + 'px');
            this.open = true;
            this.flyoutElement.classList.add('open-animation');
            this.slotElement && this.slotElement.setAttribute('open', 'true');
        }
    }

    @Method()
    public async closeFlyout(): Promise<void> {
        if (this.open) {
            this.open = false;
            this.flyoutElement.classList.add('close-animation');
            this.slotElement && this.slotElement.setAttribute('open', 'false');
            this.flyoutClosed.emit();
        }
    }

    private updateFlyoutPosition(): void {
        const viewportWidth = document.body.offsetWidth;
        const xPositionRight = viewportWidth - this.buttonElement.getBoundingClientRect().right;
        const xPositionLeft = this.buttonElement.getBoundingClientRect().left;
        let flyoutRight;
        let flyoutLeft;

        // visit grid.scss for referecne of numbers
        if (viewportWidth > 1920 && !this.ignoreViewportWidth) {
            const gridMargin = (viewportWidth - 1760) / 2;

            flyoutRight = 'calc(-' + xPositionRight + 'px + ' + gridMargin + 'px)';
            flyoutLeft = 'calc(-' + xPositionLeft + 'px + ' + gridMargin + 'px)';
        } else {
            flyoutRight = 'calc(-' + xPositionRight + 'px + var(--grid-margin))';
            flyoutLeft = 'calc(-' + xPositionLeft + 'px + var(--grid-margin))';
        }

        this.flyoutElement.style.setProperty('--right', flyoutRight);
        this.flyoutElement.style.setProperty('--left', flyoutLeft);
    }

    private buttonElement: HTMLButtonElement;
    private flyoutElement: HTMLDivElement;

    componentDidRender(): void {
        this.updateFlyoutPosition();
    }

    componentDidLoad(): void {
        this.onResize = this.updateFlyoutPosition;

        this.flyoutElement.addEventListener('animationend', () => {
            this.flyoutElement.classList.remove('open-animation');
            this.flyoutElement.classList.remove('close-animation');

            this.flyoutElement.classList.toggle('open', this.open);
            this.flyoutElement.classList.toggle('close', !this.open);
        });

        this.slotElement = this.el.querySelector('*');
    }

    render() {
        return (
            <Host>
                {this.username && (
                    <div class="username" onClick={this.toggleFlyout}>
                        {this.username}
                    </div>
                )}
                <button
                    class={{ 'header-button': true, 'open': this.open }}
                    onClick={this.toggleFlyout}
                    ref={el => (this.buttonElement = el as HTMLButtonElement)}
                    aria-label={this.iconLabel}
                >
                    <sew-icon class="svg" icon={this.icon} size="l" />
                    {this.counter && <div class="counter">{this.counter}</div>}
                </button>
                <div class="flyout-content close" ref={el => (this.flyoutElement = el as HTMLDivElement)}>
                    <div class="content-wrapper">
                        <button onClick={this.toggleFlyout}>
                            <sew-icon icon="close" width="28" height="28" />
                        </button>
                        <slot />
                    </div>
                </div>
            </Host>
        );
    }
}
